// app/javascript/controllers/ats_optimizer_controller.js
//
// Coordinator for /app/ats. Owns shared state (selected resume, optimize
// in-flight, focus mode) and HTTP; delegates panel DOM work to outlet-
// connected child controllers (ats-resume-library, ats-job-description,
// ats-template-picker, ats-preview-tabs, ats-cover-letter, ats-outreach,
// ats-jd-match, ats-enrichment).
//
// Spec: docs/superpowers/specs/2026-06-05-ats-optimizer-page-redesign-design.md
import { Controller } from "@hotwired/stimulus"

const FOCUS_STORAGE_KEY = "ats-focus-mode"

const DEFAULT_TEMPLATE_SETTINGS = {
  template: "swiss-single",
  pageSize: "A4",
  accentColor: "green"
}

export default class extends Controller {
  static outlets = [
    "ats-resume-library",
    "ats-job-description",
    "ats-template-picker",
    "ats-preview-tabs",
    "ats-cover-letter",
    "ats-outreach",
    "ats-jd-match",
    "ats-enrichment"
  ]

  static targets = ["status", "panes", "focusLabel", "resumePreview",
                    "coverLetterPreview", "outreachPreview"]

  static values = {
    selectedResumeId: String,
    resultResumeId:   String,
    statusEndpoint:   { type: String, default: "/api/v1/ats_optimizer/status" },
    optimizeEndpoint: { type: String, default: "/api/v1/ats_optimizer/optimize" },
    focus:            { type: Boolean, default: false }
  }

  connect() {
    this.templateSettings = this.#loadTemplateSettings()
    this.#restoreFocus()
    this.#listenForChildEvents()
    this.loadStatus()
  }

  disconnect() {
    this.#stopListening()
  }

  // ── status ──────────────────────────────────────────────────────────────
  async loadStatus() {
    try {
      const r = await fetch(this.statusEndpointValue, { headers: { "Accept": "application/json" } })
      if (!r.ok) throw new Error(`status ${r.status}`)
      const data = await r.json()
      const providers = Array.isArray(data.providers)
        ? data.providers.map(p => p?.provider || p?.model || p?.name || p).filter(Boolean).join(", ")
        : (data.providers || "")
      this.statusTarget.textContent = `Service: ${data.status || "unknown"}${providers ? " | " + providers : ""}`
    } catch (_e) {
      this.statusTarget.textContent = "Service unavailable"
    }
  }

  // ── child event handlers ────────────────────────────────────────────────
  #listenForChildEvents() {
    this.#bound = {
      resumeSelected:        e => this.onResumeSelected(e),
      jobDescriptionChanged: e => this.onJobDescriptionChanged(e),
      optimizeRequested:     e => this.optimize(),
      resumeResultChanged:   e => this.onResumeResultChanged(e),
      templateChanged:       e => this.onTemplateChanged(e),
      coverLetterChanged:    e => this.onCoverLetterChanged(e),
      outreachChanged:       e => this.onOutreachChanged(e)
    }
    this.element.addEventListener("ats-optimizer:resume-selected", this.#bound.resumeSelected)
    this.element.addEventListener("ats-optimizer:job-description-changed", this.#bound.jobDescriptionChanged)
    this.element.addEventListener("ats-optimizer:optimize-requested", this.#bound.optimizeRequested)
    this.element.addEventListener("ats-optimizer:resume-result-changed", this.#bound.resumeResultChanged)
    this.element.addEventListener("ats-optimizer:template-changed", this.#bound.templateChanged)
    this.element.addEventListener("ats-optimizer:cover-letter-changed", this.#bound.coverLetterChanged)
    this.element.addEventListener("ats-optimizer:outreach-changed", this.#bound.outreachChanged)
  }

  #stopListening() {
    if (!this.#bound) return
    this.element.removeEventListener("ats-optimizer:resume-selected", this.#bound.resumeSelected)
    this.element.removeEventListener("ats-optimizer:job-description-changed", this.#bound.jobDescriptionChanged)
    this.element.removeEventListener("ats-optimizer:optimize-requested", this.#bound.optimizeRequested)
    this.element.removeEventListener("ats-optimizer:resume-result-changed", this.#bound.resumeResultChanged)
    this.element.removeEventListener("ats-optimizer:template-changed", this.#bound.templateChanged)
    this.element.removeEventListener("ats-optimizer:cover-letter-changed", this.#bound.coverLetterChanged)
    this.element.removeEventListener("ats-optimizer:outreach-changed", this.#bound.outreachChanged)
  }

  // Mirror the cover-letter / outreach textareas (left accordions) into the
  // right-pane preview tabs, which live outside those controllers' subtrees.
  onCoverLetterChanged({ detail }) {
    if (this.hasCoverLetterPreviewTarget) {
      this.coverLetterPreviewTarget.textContent = detail?.text || ""
    }
  }
  onOutreachChanged({ detail }) {
    if (this.hasOutreachPreviewTarget) {
      this.outreachPreviewTarget.textContent = detail?.text || ""
    }
  }

  // Template picker changed -> update settings + re-render the resume preview.
  // (buildResumeHtml reads this.templateSettings.template / accentColor.)
  onTemplateChanged({ detail }) {
    if (!detail) return
    this.templateSettings = {
      ...this.templateSettings,
      template: detail.template || this.templateSettings.template,
      pageSize: detail.pageSize || this.templateSettings.pageSize
    }
    if (this.hasResumePreviewTarget) this.#renderResumePreview()
  }

  // Push the selected resume id down to the per-resume child controllers so
  // their generate/save/analyze/apply calls target the right resume.
  #pushResumeId(id) {
    if (this.hasAtsCoverLetterOutlet) this.atsCoverLetterOutlet.resumeIdValue = id || ""
    if (this.hasAtsOutreachOutlet)    this.atsOutreachOutlet.resumeIdValue = id || ""
    if (this.hasAtsEnrichmentOutlet)  this.atsEnrichmentOutlet.resumeIdValue = id || ""
  }
  #bound = null

  onResumeSelected({ detail }) {
    this.selectedResumeIdValue = detail?.resumeId || ""
    this.#pushResumeId(this.selectedResumeIdValue)
    if (this.hasAtsPreviewTabsOutlet) this.atsPreviewTabsOutlet.setJdScore(null)
    if (this.selectedResumeIdValue) {
      this.fetchResume(this.selectedResumeIdValue)
    } else {
      // No resume selected (e.g. after deleting the active one): clear preview.
      this.#resumeData = null
      this.#jobKeywords = {}
      if (this.hasResumePreviewTarget) {
        this.resumePreviewTarget.innerHTML =
          '<p class="text-center text-on-surface-variant font-body-sm">Upload a resume to see the preview here.</p>'
      }
    }
  }

  // Hydrate the preview panes for the selected resume (Resume View + any
  // saved Cover Letter / Outreach). Mirrors the legacy fetchResume: GET
  // /api/v1/ats_optimizer/resumes?resume_id=<id> returns
  // { data: { filename, processed_resume, cover_letter, outreach_message } }.
  // cover_letter / outreach_message are blank for a freshly-uploaded resume
  // until "Optimize Match" (or the per-panel Generate) populates them.
  async fetchResume(resumeId) {
    try {
      const r = await fetch(
        `/api/v1/ats_optimizer/resumes?resume_id=${encodeURIComponent(resumeId)}`,
        { headers: { "Accept": "application/json" } }
      )
      if (!r.ok) throw new Error(`fetch resume HTTP ${r.status}`)
      const body = await r.json()
      const resume = body.data || {}
      this.#pushResumeId(resumeId)
      this.#resumeData = resume.processed_resume || null
      this.#jobKeywords = {}
      this.#renderResumePreview()
      if (this.hasAtsCoverLetterOutlet) {
        this.atsCoverLetterOutlet.apply({ cover_letter: resume.cover_letter || "" })
      }
      if (this.hasAtsOutreachOutlet) {
        this.atsOutreachOutlet.apply({ outreach_message: resume.outreach_message || "" })
      }
    } catch (e) {
      this.statusTarget.textContent = `Failed to load resume: ${e.message}`
    }
  }

  onJobDescriptionChanged({ detail }) {
    // Coordinator gets to react if needed (e.g. analytics, debounced optimize hints).
    // For now, the child manages its own button-disabled state.
  }

  onResumeResultChanged({ detail }) {
    const id = detail?.resumeId || this.selectedResumeIdValue
    if (!id) return
    this.resultResumeIdValue = id
    // Enrichment apply changed the stored resume — re-fetch + re-render preview.
    this.fetchResume(id)
  }

  // ── optimize (3-step + poll, matches legacy lines 308-346) ──────────────
  async optimize() {
    if (!this.hasAtsJobDescriptionOutlet) return
    const jdText = this.atsJobDescriptionOutlet.value
    if (!jdText) return
    if (!this.selectedResumeIdValue) {
      this.statusTarget.textContent = "Select a resume first."
      return
    }

    try {
      this.statusTarget.textContent = "Uploading job description..."
      const jobPayload = await this.#postJson("/api/v1/ats_optimizer/jobs/upload", {
        job_descriptions: [jdText],
        resume_id: this.selectedResumeIdValue
      })
      const jobId = jobPayload.job_id?.[0]
      if (!jobId) throw new Error("Job upload failed")

      this.statusTarget.textContent = "Queueing optimization..."
      const runPayload = await this.#postJson("/api/v1/ats_optimizer/runs", {
        resume_id: this.selectedResumeIdValue,
        job_id: jobId
      })
      const runId = runPayload.run_id
      if (!runId) throw new Error("Optimization could not be started")

      this.statusTarget.textContent = "Optimizing resume..."
      const data = await this.#pollRun(runId)
      this.#applyOptimizeResult(data)
      this.statusTarget.textContent = "Optimization complete"
    } catch (e) {
      this.statusTarget.textContent = `Optimize failed: ${e.message}`
    }
  }

  regenerate() { return this.optimize() }

  async #pollRun(runId) {
    const startedAt = Date.now()
    let delay = 2000
    const maxDelay = 8000
    const timeoutMs = 10 * 60 * 1000

    while (true) {
      const r = await fetch(`/api/v1/ats_optimizer/runs/${encodeURIComponent(runId)}`,
                            { headers: { "Accept": "application/json" } })
      if (!r.ok) throw new Error(`poll HTTP ${r.status}`)
      const payload = await r.json()
      if (payload.status === "completed") return payload.data || {}
      if (payload.status === "failed") throw new Error(payload.error || "Optimization failed")
      if (Date.now() - startedAt > timeoutMs) throw new Error("Optimization timed out")
      const secs = Math.floor((Date.now() - startedAt) / 1000)
      this.statusTarget.textContent = `Optimizing resume... (${payload.status}, ${secs}s)`
      await new Promise(res => setTimeout(res, delay))
      delay = Math.min(Math.round(delay * 1.2), maxDelay)
    }
  }

  async #postJson(url, body) {
    const r = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content
      },
      body: JSON.stringify(body)
    })
    if (!r.ok) throw new Error(`POST ${url} HTTP ${r.status}`)
    return r.json()
  }

  #applyOptimizeResult(data) {
    const payload = data || {}
    this.resultResumeIdValue = payload.resume_id || ""
    this.#pushResumeId(this.resultResumeIdValue || this.selectedResumeIdValue)
    this.#resumeData = payload.resume_preview || null
    this.#jobKeywords = payload.job_keywords || {}
    if (this.hasAtsCoverLetterOutlet) this.atsCoverLetterOutlet.apply(payload)
    if (this.hasAtsOutreachOutlet)    this.atsOutreachOutlet.apply(payload)
    // Two ats-jd-match instances (left accordion + right-pane detail) — update both.
    const jdPayload = {
      job_keywords: payload.job_keywords || {},
      resume_text: this.getResumePlainText(this.#resumeData),
      jd_text: this.hasAtsJobDescriptionOutlet ? this.atsJobDescriptionOutlet.value : ""
    }
    this.atsJdMatchOutlets.forEach(o => o.apply(jdPayload))
    if (this.hasAtsPreviewTabsOutlet) {
      // Derive the score the same way ats-jd-match does, so the chip and
      // the accordion progress bar always agree.
      this.atsPreviewTabsOutlet.setJdScore(
        this.#computeMatchRate(payload.job_keywords || {}, this.#resumeData)
      )
    }
    if (this.hasResumePreviewTarget) this.#renderResumePreview()
  }

  // Internal state (NOT Stimulus values — these aren't reactive).
  #resumeData = null
  #jobKeywords = {}

  // Derive match rate from categorized job_keywords vs resume plain text.
  // Mirrors legacy updateMatchMetrics + flattenKeywords (lines 843-855 +
  // wherever flattenKeywords is defined). Pure function; no DOM.
  #computeMatchRate(jobKeywords, resumeData) {
    const flat = Object.values(jobKeywords || {})
      .flatMap(v => Array.isArray(v) ? v : [])
      .filter(Boolean)
    if (!flat.length) return null
    const resumeText = (this.getResumePlainText(resumeData) || "").toLowerCase()
    const matches = flat.filter(k => resumeText.includes(String(k).toLowerCase())).length
    return matches / flat.length
  }

  // Resume preview rendering. The buildResumeHtml-family of helpers is
  // ported verbatim from the legacy controller in Step 2 of this task
  // (legacy lines 856-end). Cohesive and self-contained — a follow-up
  // PR can extract them to a dedicated ats-resume-preview controller.
  // Note: the resume preview data lives in this.#resumeData (set by
  // #applyOptimizeResult); the highlight keyword set is the flattened
  // job_keywords.
  // Server-rendered preview: fetch the chosen builder template HTML for the
  // selected (or optimized) resume + page size and inject it. Replaces the old
  // client-side buildResumeHtml path.
  async #renderResumePreview() {
    if (!this.hasResumePreviewTarget) return
    const id = this.resultResumeIdValue || this.selectedResumeIdValue
    if (!id) return
    const tpl = (this.templateSettings && this.templateSettings.template) || "classic"
    const ps  = (this.templateSettings && this.templateSettings.pageSize) || "A4"
    try {
      const url = `/app/ats/resume_preview?resume_id=${encodeURIComponent(id)}` +
                  `&template=${encodeURIComponent(tpl)}&page_size=${encodeURIComponent(ps)}`
      const r = await fetch(url, { headers: { "Accept": "text/html" } })
      if (!r.ok) throw new Error(`preview HTTP ${r.status}`)
      this.resumePreviewTarget.innerHTML = await r.text()
      this.#applyPageSizeAspect(ps)
    } catch (e) {
      if (this.hasStatusTarget) this.statusTarget.textContent = `Preview failed: ${e.message}`
    }
  }

  #applyPageSizeAspect(pageSize) {
    if (!this.hasResumePreviewTarget) return
    this.resumePreviewTarget.dataset.pageSize = pageSize === "LETTER" ? "LETTER" : "A4"
  }

  // ── focus mode ──────────────────────────────────────────────────────────
  toggleFocusMode() {
    this.focusValue = !this.focusValue
  }

  focusValueChanged(value) {
    if (this.hasPanesTarget) this.panesTarget.dataset.focus = value ? "true" : "false"
    if (this.hasFocusLabelTarget) this.focusLabelTarget.textContent = value ? "Exit Focus" : "Focus Mode"
    try { localStorage.setItem(FOCUS_STORAGE_KEY, value ? "1" : "0") } catch (_e) {}
  }

  #restoreFocus() {
    try {
      this.focusValue = localStorage.getItem(FOCUS_STORAGE_KEY) === "1"
    } catch (_e) {
      this.focusValue = false
    }
  }

  // ── PDF download ────────────────────────────────────────────────────────
  downloadResumePdf() {
    const id = this.resultResumeIdValue || this.selectedResumeIdValue
    if (!id) { if (this.hasStatusTarget) this.statusTarget.textContent = "Select a resume first."; return }
    const tpl = (this.templateSettings && this.templateSettings.template) || "classic"
    const ps  = (this.templateSettings && this.templateSettings.pageSize) || "A4"
    window.location.href =
      `/api/v1/ats_optimizer/resumes/${encodeURIComponent(id)}/pdf` +
      `?template=${encodeURIComponent(tpl)}&pageSize=${encodeURIComponent(ps)}`
  }

  // ── template settings (read-only; ats-template-picker owns writes) ──────
  // The resume-preview helpers below need template + accentColor to render
  // the right class names and CSS variables. We load from the same
  // localStorage key the picker writes ("atsOptimizerTemplateSettings") so
  // the preview stays in sync with the user's last template choice.
  #loadTemplateSettings() {
    try {
      const stored = localStorage.getItem("atsOptimizerTemplateSettings")
      const parsed = stored ? JSON.parse(stored) : {}
      return { ...DEFAULT_TEMPLATE_SETTINGS, ...parsed }
    } catch {
      return { ...DEFAULT_TEMPLATE_SETTINGS }
    }
  }

  // ── ported verbatim from legacy lines 856-end ───────────────────────────
  buildResumeHtml(data, { highlight = false, keywords = [] } = {}) {
    const template = this.templateSettings.template
    const isTwoColumn = template.includes("two-column")
    const isModern = template.includes("modern")
    const personal = data.personalInfo || {}
    const sections = this.buildSections(data, template)
    const styleVars = this.buildStyleVars()

    const header = `
      <header class="ats-resume-header">
        <div class="ats-resume-name">${this.escapeHtml(personal.name || "Resume")}</div>
        ${personal.title ? `<div class="ats-resume-title">${this.escapeHtml(personal.title)}</div>` : ""}
        ${isModern ? `<div class="ats-resume-underline"></div>` : ""}
        <div class="ats-resume-meta">
          ${[personal.email, personal.phone, personal.location, personal.website, personal.linkedin, personal.github]
            .filter(Boolean)
            .map(item => `<span>${this.escapeHtml(item)}</span>`)
            .join("<span class=\"ats-resume-sep\">•</span>")}
        </div>
      </header>
    `

    const renderSection = section => {
      const titleClass = isModern
        ? (section.column === "side" ? "ats-resume-section-title-accent-sm" : "ats-resume-section-title-accent")
        : (section.column === "side" ? "ats-resume-section-title-sm" : "ats-resume-section-title")

      const body = section.type === "text"
        ? `<p class="ats-resume-text">${this.formatText(section.text, highlight, keywords)}</p>`
        : section.type === "list"
          ? `<ul class="ats-resume-skill-list">
              ${section.items.map(item => `<li class="ats-resume-skill">${this.formatText(item, highlight, keywords)}</li>`).join("")}
            </ul>`
          : section.items.map(item => this.renderResumeItem(item, highlight, keywords)).join("")

      return `
        <section class="ats-resume-section">
          <h3 class="${titleClass}">${this.escapeHtml(section.title)}</h3>
          ${body || ""}
        </section>
      `
    }

    const mainSections = sections.filter(section => section.column === "main").map(renderSection).join("")
    const sideSections = sections.filter(section => section.column === "side").map(renderSection).join("")

    if (isTwoColumn) {
      return `
        <div class="ats-resume ats-resume--${template}" style="${styleVars}">
          ${header}
          <div class="ats-resume-grid">
            <div class="ats-resume-main">${mainSections}</div>
            <div class="ats-resume-side">${sideSections}</div>
          </div>
        </div>
      `
    }

    return `
      <div class="ats-resume ats-resume--${template}" style="${styleVars}">
        ${header}
        ${mainSections}
      </div>
    `
  }

  renderResumeItem(item, highlight, keywords) {
    const description = Array.isArray(item.description) ? item.description : []
    const bullets = description
      .map(text => `<li>${this.formatText(text, highlight, keywords)}</li>`)
      .join("")

    return `
      <div class="ats-resume-entry">
        <div class="ats-resume-entry-header">
          <div>
            <div class="ats-resume-entry-title">${this.formatText(item.title, highlight, keywords)}</div>
            ${item.subtitle ? `<div class="ats-resume-entry-subtitle">${this.formatText(item.subtitle, highlight, keywords)}${item.location ? ` · ${this.formatText(item.location, highlight, keywords)}` : ""}</div>` : ""}
          </div>
          ${item.years ? `<div class="ats-resume-entry-date">${this.escapeHtml(item.years)}</div>` : ""}
        </div>
        ${bullets ? `<ul class="ats-resume-entry-list">${bullets}</ul>` : ""}
      </div>
    `
  }

  buildSections(data, template) {
    const defaultSections = [
      { key: "summary", displayName: "Summary", order: 1 },
      { key: "workExperience", displayName: "Experience", order: 2 },
      { key: "education", displayName: "Education", order: 3 },
      { key: "personalProjects", displayName: "Projects", order: 4 },
      { key: "technicalSkills", displayName: "Technical Skills", order: 5 },
      { key: "languages", displayName: "Languages", order: 6 },
      { key: "certificationsTraining", displayName: "Certifications", order: 7 },
      { key: "awards", displayName: "Awards", order: 8 }
    ]

    const meta = Array.isArray(data.sectionMeta) && data.sectionMeta.length
      ? data.sectionMeta.filter(section => section.isVisible !== false).sort((a, b) => (a.order || 0) - (b.order || 0))
      : defaultSections

    const customSections = data.customSections || {}

    return meta.reduce((sections, section) => {
      const key = section.key || section.id
      if (!key) return sections
      const displayName = section.displayName || section.display_name || this.humanizeKey(key)
      const column = this.resolveColumn(template, key)

      if (key === "summary" && data.summary) {
        sections.push({ key, title: displayName, type: "text", text: data.summary, column })
        return sections
      }

      if (key === "workExperience" && Array.isArray(data.workExperience) && data.workExperience.length) {
        sections.push({
          key,
          title: displayName,
          type: "items",
          column,
          items: data.workExperience.map(item => ({
            title: item.title || "",
            subtitle: item.company || "",
            location: item.location || "",
            years: item.years || "",
            description: Array.isArray(item.description) ? item.description : []
          }))
        })
        return sections
      }

      if (key === "education" && Array.isArray(data.education) && data.education.length) {
        sections.push({
          key,
          title: displayName,
          type: "items",
          column,
          items: data.education.map(item => ({
            title: item.institution || "",
            subtitle: item.degree || "",
            location: "",
            years: item.years || "",
            description: Array.isArray(item.description) ? item.description : []
          }))
        })
        return sections
      }

      if (key === "personalProjects" && Array.isArray(data.personalProjects) && data.personalProjects.length) {
        sections.push({
          key,
          title: displayName,
          type: "items",
          column,
          items: data.personalProjects.map(item => ({
            title: item.name || "",
            subtitle: item.role || "",
            location: "",
            years: item.years || "",
            description: Array.isArray(item.description) ? item.description : []
          }))
        })
        return sections
      }

      if (["technicalSkills", "languages", "certificationsTraining", "awards"].includes(key)) {
        const list = data.additional?.[key] || []
        if (list.length) {
          sections.push({ key, title: displayName, type: "list", items: list, column })
        }
        return sections
      }

      const custom = customSections[key]
      if (!custom) return sections

      const sectionType = (custom.sectionType || "").toLowerCase()
      if (sectionType.includes("text")) {
        if (custom.text) {
          sections.push({ key, title: displayName, type: "text", text: custom.text, column })
        }
        return sections
      }

      if (sectionType.includes("string")) {
        const list = Array.isArray(custom.strings) ? custom.strings : []
        if (list.length) {
          sections.push({ key, title: displayName, type: "list", items: list, column })
        }
        return sections
      }

      const items = Array.isArray(custom.items) ? custom.items : []
      if (items.length) {
        sections.push({
          key,
          title: displayName,
          type: "items",
          column,
          items: items.map(item => ({
            title: item.title || "",
            subtitle: item.subtitle || "",
            location: item.location || "",
            years: item.years || "",
            description: Array.isArray(item.description) ? item.description : []
          }))
        })
      }

      return sections
    }, [])
  }

  resolveColumn(template, key) {
    if (!template.includes("two-column")) return "main"
    if (["education", "technicalSkills", "languages", "awards"].includes(key)) return "side"
    return "main"
  }

  buildStyleVars() {
    const accentMap = {
      blue: "#1D4ED8",
      green: "#B9FF66",
      orange: "#F59E0B",
      red: "#E11D48"
    }
    const accentLightMap = {
      blue: "#DBEAFE",
      green: "#ECFCCB",
      orange: "#FDE68A",
      red: "#FECACA"
    }

    const accent = accentMap[this.templateSettings.accentColor] || accentMap.green
    const accentLight = accentLightMap[this.templateSettings.accentColor] || accentLightMap.green

    return [
      `--ats-resume-accent:${accent}`,
      `--ats-resume-accent-light:${accentLight}`,
      `--ats-resume-font-size:11px`,
      `--ats-resume-line-height:1.35`,
      `--ats-resume-header-scale:1.64`
    ].join(";")
  }

  formatText(text, highlight, keywords) {
    if (!highlight) return this.escapeHtml(text || "")
    return this.highlightText(text || "", keywords)
  }

  highlightText(text, keywords) {
    const safe = this.escapeHtml(text || "")
    if (!keywords.length) return safe
    const pattern = new RegExp(`(${keywords.map(keyword => this.escapeRegExp(keyword)).join("|")})`, "gi")
    return safe.replace(pattern, '<mark class="ats-highlight">$1</mark>')
  }

  escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  getResumePlainText(data) {
    if (!data) return ""
    const parts = []
    const personal = data.personalInfo || {}
    parts.push(personal.name, personal.title, personal.email, personal.location)
    parts.push(data.summary)
    ;(data.workExperience || []).forEach(item => {
      parts.push(item.title, item.company, item.location, item.years)
      ;(item.description || []).forEach(desc => parts.push(desc))
    })
    ;(data.education || []).forEach(item => {
      parts.push(item.institution, item.degree, item.years)
      ;(item.description || []).forEach(desc => parts.push(desc))
    })
    ;(data.personalProjects || []).forEach(item => {
      parts.push(item.name, item.role, item.years)
      ;(item.description || []).forEach(desc => parts.push(desc))
    })
    const additional = data.additional || {}
    parts.push(...(additional.technicalSkills || []))
    parts.push(...(additional.languages || []))
    parts.push(...(additional.certificationsTraining || []))
    parts.push(...(additional.awards || []))
    return parts.filter(Boolean).join(" ").toLowerCase()
  }

  // Utility helpers used by the ported resume-preview methods above.
  // Ported from legacy controller (humanizeKey: 1163-1168, escapeHtml: 1228-1236).
  humanizeKey(key) {
    return String(key || "")
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, char => char.toUpperCase())
  }

  escapeHtml(value) {
    const stringValue = String(value || "")
    return stringValue
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
